import React, { useEffect, useState } from "react";
import api from "./api";
import { GOLD, tf, ts, DAYS } from "./data";

export default function AdminPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/analytics/summary");
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
      }
    })();
  }, []);

  if (err) {
    return (
      <div className="jp-page" data-testid="admin-error">
        <div className="jp-page-inner">
          <p style={{ ...tf, color: "#ff6868" }}>Could not load analytics: {String(err)}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="jp-page">
        <div className="jp-page-inner" style={{ ...tf, color: "rgba(255,255,255,0.4)" }}>
          Loading analytics…
        </div>
      </div>
    );
  }

  const maxTs = Math.max(1, ...data.timeseries.map((x) => x.count));
  const maxDay = Math.max(1, ...data.per_day.map((x) => x.count));

  return (
    <div className="jp-page" data-testid="admin-view">
      <div className="jp-page-inner">
        <h2 className="jp-h2">Analytics</h2>
        <p className="jp-h2-sub">Private dashboard · live from your MongoDB</p>

        <div className="jp-kpi-grid" data-testid="kpi-row">
          <KPI label="Total Page Views" value={data.total_views} emoji="👁️" />
          <KPI label="Unique Visitors" value={data.unique_visitors} emoji="🧍" />
          <KPI label="Photos Stored" value={data.photo_count} emoji="📸" />
          <KPI label="Days Opened" value={data.per_day.reduce((s, x) => s + x.count, 0)} emoji="📖" />
        </div>

        <div className="jp-admin-grid">
          <div className="jp-admin-card">
            <h3 className="jp-h3">Page views · last 14 days</h3>
            {data.timeseries.length === 0 ? (
              <p className="jp-empty">No views yet</p>
            ) : (
              <div className="jp-bars" data-testid="timeseries-chart">
                {data.timeseries.map((x) => (
                  <div key={x.date} className="jp-bar-col" title={`${x.date}: ${x.count}`}>
                    <div className="jp-bar" style={{ height: `${(x.count / maxTs) * 100}%` }} />
                    <div className="jp-bar-lbl">{x.date.slice(5)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="jp-admin-card">
            <h3 className="jp-h3">Most viewed days</h3>
            {data.per_day.length === 0 ? (
              <p className="jp-empty">No day opens yet</p>
            ) : (
              <div className="jp-day-bars" data-testid="day-chart">
                {data.per_day.map((d) => {
                  const day = DAYS.find((x) => x.day === d.day);
                  return (
                    <div key={d.day} className="jp-day-bar-row">
                      <div className="jp-day-bar-label">
                        {day?.emoji} Day {d.day} · {day?.city || "?"}
                      </div>
                      <div className="jp-day-bar-wrap">
                        <div className="jp-day-bar-fill" style={{ width: `${(d.count / maxDay) * 100}%` }} />
                      </div>
                      <div className="jp-day-bar-val">{d.count}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="jp-admin-card">
          <h3 className="jp-h3">Recent activity</h3>
          {data.recent.length === 0 ? (
            <p className="jp-empty">No activity yet</p>
          ) : (
            <table className="jp-table" data-testid="recent-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Event</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((r) => (
                  <tr key={r._id}>
                    <td>{new Date(r.ts).toLocaleString()}</td>
                    <td><span className="jp-evt-pill">{r.event}</span></td>
                    <td style={{ color: "rgba(255,255,255,0.5)" }}>
                      {r.day ? `Day ${r.day}` : r.path || ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {data.referrers.length > 0 && (
          <div className="jp-admin-card" style={{ marginTop: "1rem" }}>
            <h3 className="jp-h3">Top referrers</h3>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {data.referrers.map((r) => (
                <li key={r.referrer} style={{ ...tf, color: "rgba(255,255,255,0.55)", fontSize: "0.82rem", padding: "0.4rem 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                  <span style={{ color: GOLD }}>{r.count}</span> · {r.referrer}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, emoji }) {
  return (
    <div className="jp-kpi" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div style={{ fontSize: "1.3rem" }}>{emoji}</div>
      <div style={{ ...ts, color: GOLD, fontSize: "1.9rem", fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
      <div style={{ ...tf, color: "rgba(255,255,255,0.4)", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>
        {label}
      </div>
    </div>
  );
}
